import React, { createElement, useMemo, useRef } from 'react';
import { DragItem, TileInfo, openTileUrl, TopicsPopupProps } from 'app/home/index.functions';
import { TileSection } from 'app/home/tile/TilePanel.functions';
import { camelCase } from 'lodash';
import { UserAuth } from 'store/auth';
import { useAppSelector } from 'store/hooks';
import { TileName } from 'app/home/tile/components/TileName';
import { TileFavorite } from 'app/home/tile/components/TileFavorite';
import { TileEdit } from 'app/home/tile/components/TileEdit';
import { TileLock } from 'app/home/tile/components/TileLock';
import { ReportToolSection } from 'app/home/tile/components/ReportToolSection';
import { ReportToolSectionLine } from 'app/home/tile/components/ReportToolSectionLine';
import { ReportToolSectionMultiLine } from 'app/home/tile/components/ReportToolSectionMultiLine';
import { TileInfoIcon } from 'components/tile-info-icon';
import { Tile4FavoriteData } from 'app/home/index';
import { ThumbnailLoader } from 'app/home/useThumbnailLoader';
import { useDrag, useDrop } from 'react-dnd';
import { Identifier } from 'dnd-core';
import { useHomeContext } from 'app/home/context';
import { Box, SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatPercent } from 'tools/tools';
import useBundleTranslation from 'i18n';
import styles from './TileComponent.styles';
import { useSelector } from 'react-redux';
import { listTemplate, multiListTemplate, tileTemplate } from 'app/home/tile/TileTemplates';
import { useMiNavigation } from 'hooks/useNavigation';

const TileComponentsRegistry: { [key: string]: any } = {
    TileName: TileName,
    TileFavorite: TileFavorite,
    TileEdit: TileEdit,
    TileLock: TileLock,
    TileInfo: TileInfoIcon,
    ReportToolSection: ReportToolSection,
    ReportToolSectionLine: ReportToolSectionLine,
    ReportToolSectionMultiLine: ReportToolSectionMultiLine,
    box: Box,
};

export default function TileComponent({
    index,
    moveTile,
    tile,
    section,
    style,
    thumbnailLoader,
    setTile4Favorite,
    customFieldsDisplayOnTileInfo,
    setTilePreview,
    view,
    sx,
}: {
    index: number;
    moveTile: (sectionKey: string, udei_id: string, hover_udei_id: string) => void;
    tile: TileInfo;
    section: TileSection;
    style?: any;
    thumbnailLoader: ThumbnailLoader;
    setTile4Favorite: (data: Tile4FavoriteData | null) => void;
    customFieldsDisplayOnTileInfo: { [key: string]: string };
    setTilePreview: (tile: TileInfo | null) => void;
    view?: 'tile' | 'list' | 'multi-list';
    sx?: SxProps;
}) {
    const ctx = useHomeContext();
    const [
        homePageState,
        trashBucketRef,
        tileIcons,
        settings,
        favorites,
        folders,
        hierarchyFolders,
        setAccessDeniedPopupTile,
        handleContextMenu,
        isEmbedded,
        newTab,
        navigationBack,
    ] = [
        ctx.homePageState,
        ctx.trashBucketRef,
        ctx.data.tileIcons,
        ctx.homePageSettings,
        ctx.favorites,
        ctx.folders,
        ctx.data.hierarchyFolders,
        ctx.setAccessDeniedPopupTile,
        ctx.handleContextMenu,
        ctx.isEmbedded,
        ctx.newTab,
        ctx.navigationBack,
    ];
    const brandThemeProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties);

    const currentCatalogId = ctx.currentCatalogId;

    const template = useMemo(() => {
        if (
            homePageState?.filterState?.view == 'list' &&
            undefined !== settings?.theme?.catalog_list_column_count &&
            settings?.theme?.catalog_list_column_count > 1
        ) {
            let template = structuredClone(multiListTemplate);
            if ('Y' === settings.theme?.catalog_list_display_content_type_icons) {
                template.children.unshift({
                    component: 'ReportToolSectionMultiLine',
                });
            }
            return template;
        }

        return homePageState?.filterState?.view == 'list'
            ? structuredClone(listTemplate)
            : structuredClone(tileTemplate);
    }, [homePageState?.filterState?.view, settings?.theme?.catalog_list_column_count]);

    const appTheme = useTheme();
    const navigate = useMiNavigation();
    if (null == template) return null;

    const userAuth: UserAuth = useAppSelector((state) => state.auth);

    const ThumbnailImage = function ({
        style,
        src,
        alt,
        tile,
        isListView,
    }: {
        style?: any;
        src?: string;
        alt?: string;
        tile: TileInfo;
        isListView: boolean;
    }) {
        let defaultStyle = {};
        const { t } = useBundleTranslation('app/home');

        if (undefined === thumbnailLoader) return null;

        if (!isListView && ['metric'].includes(tile.element_type || '')) {
            return (
                <Box className={'tile-element__thumbnail-wrapper'}>
                    <Box className={'tile-element__thumbnail-metric'}>
                        <Box className={'tile-element__thumbnail-metric-image'}>
                            <img
                                src={thumbnailLoader.getImage(
                                    tile.user_dashboard_element_instance_id || '',
                                    tile.element_type,
                                )}
                                className={'thumbnail_' + tile!.user_dashboard_element_instance_id}
                            />
                        </Box>

                        <Box className={'tile-element__thumbnail-metric-ptc-variance'}>
                            {null != tile.metric_tile_display_pct_variance && 'N' !== tile.has_access && (
                                <Box className={'dashboardValuemark'}>
                                    <Box component={'span'} className={'dashboardCompareValue'}>
                                        {tile.metric_tile_display_pct_variance > 0 ? '+' : '-'}
                                        {formatPercent(String(Math.abs(tile.metric_tile_display_pct_variance)))}
                                    </Box>
                                    {tile.metric_unit_of_measure != null
                                        ? ' ' +
                                          (tile.total_forecast_amount_formatted != null ? t('label_projected') : '') +
                                          ' ' +
                                          tile.metric_unit_of_measure
                                        : null}
                                </Box>
                            )}
                        </Box>

                        <Box className={'tile-element__thumbnail-metric-value'}>
                            {'N' !== tile.has_access &&
                                ('average' == tile.metric_display_on_tile
                                    ? tile.metric_display_on_tile_prefix +
                                      '' +
                                      tile.metric_last_moving_average_value_formatted
                                    : tile.total_forecast_amount_formatted != null
                                      ? tile.total_forecast_amount_formatted
                                      : tile.last_measurement_value_formatted != null
                                        ? tile.last_measurement_value_formatted
                                        : '')}
                        </Box>
                    </Box>
                </Box>
            );
        }

        if (('internal report' == tile.element_type || '') && tile.report_rows && tile.report_rows !== '') {
            return (
                <Box className={'tile-element__thumbnail-wrapper'}>
                    <Box className={'tile-element__thumbnail-report-rows'}>
                        <Box className={'tile-element__thumbnail-report-rows-text'}>
                            {tile.report_rows > '0' && (
                                <Box className={'tile-element__thumbnail-report-rows-count'} component={'span'}>
                                    {tile.report_rows + ' '}
                                </Box>
                            )}
                            <Box className={'tile-element__thumbnail-report-rows-label'} component={'span'}>
                                {tile.report_rows > '0'
                                    ? tile.report_rows > '1'
                                        ? tile.report_multiple_units_label
                                        : tile.report_single_unit_label
                                    : undefined != tile.report_no_units_label && tile.report_no_units_label > ''
                                      ? tile.report_no_units_label
                                      : t('no_for_no_rows') + ' ' + tile.report_multiple_units_label}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            );
        }

        return (
            <Box className={'tile-element__thumbnail-wrapper'}>
                <img
                    src={thumbnailLoader.getImage(tile.user_dashboard_element_instance_id || '', tile.element_type)}
                    className={'tile-element__thumbnail-img thumbnail_' + tile!.user_dashboard_element_instance_id}
                    alt={alt}
                    style={{ ...style, ...defaultStyle }}
                />
            </Box>
        );
    };

    const processComponentName = (componentName: string) => {
        if (TileComponentsRegistry.hasOwnProperty(componentName)) {
            return TileComponentsRegistry[componentName];
        } else if (componentName == 'ThumbnailImage') {
            return ThumbnailImage;
        } else return componentName;
    };

    const processContent = (content: string) => {
        return content.replace('{{element_dashboard_name}}', tile.element_dashboard_name || '');
    };

    const processStyles = (s: any) => {
        if (!s) return {};

        let styles: any = {};
        for (const k in s) {
            styles[camelCase(k)] = s[k];
        }

        return styles;
    };

    const processAttributes = (te: any) => {
        let attributes: any = {};
        if (te.attr) for (const k in te.attr) attributes[k] = processContent(te.attr[k]);

        return attributes;
    };

    const showTilePreview = () => {
        const showTilePreview = 'Y' === settings.parameters?.show_tile_preview_ind;
        const hasPreview =
            (settings.parameters?.SKIP_PREVIEW_FOR_EXTERNAL_REPORTS === 'N' &&
                tile.element_type === 'external report') ||
            (settings.parameters?.SKIP_PREVIEW_FOR_EXTERNAL_CONTENT === 'N' &&
                tile.element_type === 'other external content') ||
            !(tile.element_type === 'external report' || tile.element_type === 'other external content');

        return showTilePreview && hasPreview;
    };

    const processTemplateComponent = (te: any, parent_k: string, style?: any, props?: any) => {
        let componentProps = {
            ...processAttributes(te),
            ...(props || {}),
            key: parent_k,
            style: { ...processStyles(te.style), ...(style || {}) },
            className: te.className,
        };

        if ('tc_0' == parent_k) {
            const ref = useRef<HTMLDivElement>(null);
            const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
                accept: 'tile',
                collect(monitor) {
                    return {
                        handlerId: monitor.getHandlerId(),
                    };
                },
                drop(item: DragItem, monitor) {
                    moveTile(
                        section.k,
                        item.tile.user_dashboard_element_instance_id ?? '',
                        tile.user_dashboard_element_instance_id ?? '',
                    );
                },
            });

            const [{ isDragging }, drag] = useDrag(() => ({
                type: 'tile',
                item: () => {
                    return {
                        tile,
                        index,
                        sectionKey: section.k,
                    };
                },
                collect: (monitor) => ({
                    isDragging: monitor.isDragging(),
                }),
                end: (item, monitor) => {
                    const didDrop = monitor.didDrop();

                    if (!didDrop) {
                        moveTile(
                            section.k,
                            item.tile.user_dashboard_element_instance_id ?? '',
                            tile.user_dashboard_element_instance_id ?? '',
                        );
                    }
                },
            }));

            if (!isDragging) {
                if (trashBucketRef && trashBucketRef.current) trashBucketRef.current.style.display = 'none';
            } else if (section.k.startsWith('folder_')) {
                let folderId = parseInt(section.k.replace('folder_', ''));
                let folder = hierarchyFolders.find((f) => f.folder_id === folderId);
                if (undefined !== folder)
                    if (trashBucketRef && trashBucketRef.current)
                        trashBucketRef.current.style.display = folder?.is_edit_access_ind === 'Y' ? 'flex' : 'none';
            }
            if (!isDragging) {
                document.body.classList.remove('catalog-tile-dragging');
            } else {
                document.body.classList.add('catalog-tile-dragging');
            }

            drag(ref);
            drop(ref);
            componentProps = { ...componentProps, ref: ref };
            componentProps.onContextMenu = (e: React.MouseEvent<HTMLElement>) => handleContextMenu(e, tile);
            componentProps.style = { ...(componentProps.style || {}) };
            let stopLightClass = (() => {
                if ('1' == tile.last_stoplight_value) {
                    return 'good';
                } else if ('2' == tile.last_stoplight_value) {
                    return 'neutral';
                } else if ('3' == tile.last_stoplight_value) {
                    return 'bad';
                } else {
                    return 'default';
                }
            })();
            const imagePosition = appTheme.componentSettings.tile.imagePosition ?? 'bottom';
            componentProps.className = `tile-element--view-${view ?? 'tile'} tile-element--stoplight-${stopLightClass} ${componentProps.className ?? ''}${tile.has_access !== 'N' ? ' tile-element--lock' : ''}${tile.certified_ind === 'Y' ? ' tile-element--certified' : ''} tile-element--image-${imagePosition}`;
            te.component = 'box';
            componentProps.sx = styles.tileElement;
        }

        if (te.component[0].match(/^[A-Z]/)) {
            componentProps = {
                ...componentProps,
                tile: tile,
                userauth: userAuth,
                tileicons: tileIcons,
                setTile4Favorite: setTile4Favorite,
                settings: settings,
                customFieldsDisplayOnTileInfo: customFieldsDisplayOnTileInfo,
                favorites: favorites,
                isListView: 'list' === homePageState?.filterState?.view,
            };
        }

        return createElement(
            processComponentName(te.component),
            componentProps,
            te.children && te.children.length > 0 ? (
                <>
                    {te.children.map((c: any, k: number) => {
                        return processTemplateComponent(c, parent_k + '_' + String(k));
                    })}
                </>
            ) : te.content ? (
                processContent(te.content)
            ) : null,
        );
    };

    const timer = useRef(0);
    const props = {
        onClick: (e: React.MouseEvent) => {
            clearTimeout(timer.current);
            const handleClick = () => {
                if (!isEmbedded) {
                    if (tile.has_access !== 'N') {
                        if (showTilePreview()) {
                            setTilePreview(tile);
                        } else {
                            return openTileUrl(tile, navigate, currentCatalogId, e, newTab);
                        }
                    } else {
                        setAccessDeniedPopupTile(tile);
                    }
                } else if (navigationBack) {
                    return openTileUrl(tile, navigate, currentCatalogId, e, newTab);
                }
            };

            const handleDoubleClick = () => {
                // An embedded tile opens the Viewer only when its embed allows navigating back, exactly as a single click does.
                if (isEmbedded) {
                    return navigationBack ? openTileUrl(tile, navigate, currentCatalogId, e, newTab) : undefined;
                }
                return tile.has_access == 'N'
                    ? setAccessDeniedPopupTile(tile)
                    : openTileUrl(tile, navigate, currentCatalogId, e, newTab);
            };

            if (e.detail == 1) {
                timer.current = window.setTimeout(() => handleClick(), 200);
            } else if (e.detail == 2) {
                handleDoubleClick();
            }
        },
    };

    return processTemplateComponent(template, 'tc_0', style, props);
}
